import { defineComponent } from "vue/dist/vue.esm-bundler.js";

export function splitMessageContentSegments(content) {
    const text = String(content || "");
    const fence = /```([^\n`]*)\n([\s\S]*?)```/g;
    const segments = [];
    let lastIndex = 0;
    let match;
    while ((match = fence.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: "text", text: text.slice(lastIndex, match.index) });
        }
        segments.push({ type: "code", lang: (match[1] || "").trim(), text: match[2].replace(/\n+$/, "") });
        lastIndex = fence.lastIndex;
    }
    if (lastIndex < text.length) {
        segments.push({ type: "text", text: text.slice(lastIndex) });
    }
    return segments
        .map((seg) => (seg.type === "text" ? { ...seg, text: seg.text.replace(/^\n+|\n+$/g, "") } : seg))
        .filter((seg) => seg.type === "code" || seg.text.length > 0);
}

export const MessageList = defineComponent({
    name: "MessageList",
    props: {
        bridge: { type: Object, required: true },
        view: { type: Object, required: true },
        onAction: { type: Function, required: true },
        submitQuestion: { type: Function, required: true }
    },
    methods: {
        contentSegments(content) {
            return splitMessageContentSegments(content);
        },
        faVerificationBadge(faVerification) {
            if (!faVerification) return "";
            if (faVerification.status === "skipped" || faVerification.verdict === "skipped") return "SKIP";
            if (faVerification.status === "queued" || faVerification.verdict === "queued") return "QUEUE";
            return faVerification.verdict || "VERIFY";
        },
        faVerificationTone(faVerification) {
            if (!faVerification) return "";
            if (faVerification.status === "skipped" || faVerification.verdict === "skipped") return "is-skipped";
            if (faVerification.status === "queued" || faVerification.verdict === "queued") return "is-skipped";
            return faVerification.verdict === "PASS" ? "is-pass" : "is-fail";
        },
        faVerificationSummary(faVerification) {
            if (!faVerification) return "";
            if (faVerification.status === "skipped" || faVerification.verdict === "skipped") {
                return "L1検証はスキップされました";
            }
            if (faVerification.status === "queued" || faVerification.verdict === "queued") {
                return `${faVerification.verdictScope || "L1"}検証をキューへ投入しました`;
            }
            return faVerification.verdict === "PASS"
                ? `${faVerification.verdictScope || "L1"}検証通過（納品確認は未）`
                : `${faVerification.verdictScope || "L1"}検証で差分が残っています`;
        },
        faVerificationStats(faVerification) {
            if (!faVerification
                || faVerification.status === "skipped" || faVerification.verdict === "skipped"
                || faVerification.status === "queued" || faVerification.verdict === "queued") return [];
            const stats = [];
            if (Array.isArray(faVerification.iterations) && faVerification.iterations.length) {
                stats.push(`iter ${faVerification.iterations.length}`);
            }
            if (Number.isFinite(Number(faVerification.passCount))) {
                stats.push(`pass ${Number(faVerification.passCount)}`);
            }
            if (Number.isFinite(Number(faVerification.failCount))) {
                stats.push(`fail ${Number(faVerification.failCount)}`);
            }
            return stats;
        },
        appliedSkillLabel(skill) {
            return skill.source === "on-demand" ? `${skill.name}（参照）` : skill.name;
        },
        async copyCode(text, event) {
            const button = event?.currentTarget;
            const original = button ? button.textContent : "";
            try {
                if (navigator.clipboard?.writeText) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const area = document.createElement("textarea");
                    area.value = text;
                    document.body.appendChild(area);
                    area.select();
                    document.execCommand("copy");
                    document.body.removeChild(area);
                }
                if (button) {
                    button.textContent = "コピーしました";
                    setTimeout(() => { button.textContent = original; }, 1500);
                }
            } catch (error) {
                console.warn("copy code failed", error);
                if (button) {
                    button.textContent = "コピー失敗";
                    setTimeout(() => { button.textContent = original; }, 1500);
                }
            }
        }
    },
    template: `
        <div class="chat-panel">
            <div class="chat-log">
                <div v-if="!view.blocks.length" class="chat-log-empty">
                    チャット履歴はまだありません。プラン、要約、フロー作成、ST コード生成から始められます。
                </div>
                <template v-for="block in view.blocks" :key="block.kind === 'worklog' ? block.id : block.message.id">
                    <div v-if="block.kind === 'worklog'" class="chat-worklog">
                        <button type="button" class="chat-worklog-toggle" @click="bridge.toggleWorklog(block.id)">
                            <span class="chat-worklog-summary">{{ block.summary }}</span>
                            <span class="chat-worklog-count">{{ block.countLabel }}</span>
                        </button>
                        <div v-if="!block.collapsed" class="chat-worklog-lines">
                            <div v-for="row in block.messages" :key="row.id" class="chat-worklog-line">
                                <span class="meta">{{ row.meta || row.role || 'log' }} · {{ row.ts }}</span>
                                {{ row.content }}
                            </div>
                        </div>
                    </div>
                    <div
                        v-else
                        class="chat-message"
                        :class="[
                            block.message.role,
                            {
                                'agent-question-card': block.message.meta === 'agent-question' && block.message.question,
                                'fa-verification-card': block.message.meta === 'fa-verification' && block.message.faVerification
                            },
                            block.message.meta === 'fa-verification' && block.message.faVerification
                                ? faVerificationTone(block.message.faVerification)
                                : ''
                        ]"
                    >
                        <span class="meta">{{ block.message.role }}{{ block.message.meta ? ' · ' + block.message.meta : '' }} · {{ block.message.ts }}</span>
                        <template v-if="block.message.meta === 'agent-question' && block.message.question">
                            <div class="agent-question-header">
                                <div class="agent-question-title">{{ block.message.question.text || block.message.content }}</div>
                                <div v-if="block.message.question.reason" class="agent-question-reason">{{ block.message.question.reason }}</div>
                            </div>
                            <div v-if="block.message.question.options?.length" class="agent-question-actions">
                                <div class="agent-question-section-label">候補から選ぶ</div>
                                <button
                                    v-for="action in block.message.question.options"
                                    :key="action.questionId + ':' + action.value"
                                    type="button"
                                    class="secondary"
                                    @click="onAction(action)"
                                >
                                    {{ action.label || action.value || '回答' }}
                                </button>
                            </div>
                            <div class="agent-question-input">
                                <div class="agent-question-section-label">
                                    {{ block.message.question.options?.length ? 'その他（任意入力）' : '回答を入力' }}
                                </div>
                                <div
                                    v-if="block.message.question.options?.length"
                                    class="agent-question-input-help"
                                >
                                    候補にない内容や補足があれば入力してください。未入力でも候補だけで進められます。
                                </div>
                                <div class="agent-question-input-row">
                                    <input
                                        type="text"
                                        :placeholder="block.message.question.options?.length ? (block.message.question.placeholder || 'その他の内容を入力') : (block.message.question.placeholder || '回答を入力')"
                                        :value="view.questionDraftInput"
                                        @input="bridge.setQuestionDraftInput($event.target.value)"
                                        @keydown.enter.prevent="submitQuestion(block.message.question.id)"
                                    >
                                    <button type="button" @click="submitQuestion(block.message.question.id)">送信</button>
                                </div>
                                <div v-if="view.questionCardError" class="agent-question-inline-error">{{ view.questionCardError }}</div>
                            </div>
                        </template>
                        <template v-else-if="block.message.meta === 'fa-verification' && block.message.faVerification">
                            <div class="fa-verification-header">
                                <div class="fa-verification-title">{{ faVerificationSummary(block.message.faVerification) }}</div>
                                <span class="fa-verification-badge" :class="faVerificationTone(block.message.faVerification)">
                                    {{ faVerificationBadge(block.message.faVerification) }}
                                </span>
                            </div>
                            <div v-if="faVerificationStats(block.message.faVerification).length" class="fa-verification-stats">
                                <span
                                    v-for="stat in faVerificationStats(block.message.faVerification)"
                                    :key="stat"
                                    class="fa-verification-stat"
                                >
                                    {{ stat }}
                                </span>
                            </div>
                            <div class="chat-message-body">
                                <template v-for="(seg, segIndex) in contentSegments(block.message.content)" :key="segIndex">
                                    <div v-if="seg.type === 'code'" class="chat-code-block">
                                        <div class="chat-code-head">
                                            <span class="chat-code-lang">{{ seg.lang || 'code' }}</span>
                                            <button type="button" class="chat-code-copy" @click="copyCode(seg.text, $event)">コピー</button>
                                        </div>
                                        <pre class="chat-code-pre"><code>{{ seg.text }}</code></pre>
                                    </div>
                                    <div v-else class="chat-text-segment">{{ seg.text }}</div>
                                </template>
                            </div>
                        </template>
                        <template v-else>
                            <div v-if="block.message.appliedSkills?.length" class="chat-applied-skills">
                                <span
                                    v-for="skill in block.message.appliedSkills"
                                    :key="skill.key"
                                    class="chat-applied-skill-chip"
                                    :class="skill.source === 'on-demand' ? 'is-on-demand' : 'is-project'"
                                >
                                    {{ appliedSkillLabel(skill) }}
                                </span>
                            </div>
                            <div class="chat-message-body">
                                <template v-for="(seg, segIndex) in contentSegments(block.message.content)" :key="segIndex">
                                    <div v-if="seg.type === 'code'" class="chat-code-block">
                                        <div class="chat-code-head">
                                            <span class="chat-code-lang">{{ seg.lang || 'code' }}</span>
                                            <button type="button" class="chat-code-copy" @click="copyCode(seg.text, $event)">コピー</button>
                                        </div>
                                        <pre class="chat-code-pre"><code>{{ seg.text }}</code></pre>
                                    </div>
                                    <div v-else class="chat-text-segment">{{ seg.text }}</div>
                                </template>
                            </div>
                            <div v-if="block.message.actions?.length" class="chat-message-actions">
                                <button
                                    v-for="action in block.message.actions"
                                    :key="(action.kind || 'action') + ':' + (action.label || action.value || '')"
                                    type="button"
                                    :class="action.variant === 'primary' ? '' : 'secondary'"
                                    @click="onAction(action)"
                                >
                                    {{ action.label || action.value || action.kind || '実行' }}
                                </button>
                            </div>
                        </template>
                    </div>
                </template>
            </div>
        </div>
    `
});
